/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { ChangeEvent, FocusEvent, forwardRef, useRef } from "react";

import cn from "classnames";

import { Button } from "../Button";
import { LoadingDots } from "../Loaders";
import CalendarPickerIcon from "components/icons/CalendarPickerIcon";

import { InputInterface } from "./interface";
import s from "./Input.module.css";

const InputDate = forwardRef(
  (
    {
      isLoading,
      isMissing,
      className,
      isTodayHidden,
      ...rest
    }: Omit<InputInterface, "size">,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleRef = (node: HTMLInputElement | null) => {
      inputRef.current = node;

      if (typeof ref === "function") {
        ref(node);
      } else if (ref && "current" in ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    };

    return (
      <div className="relative w-full" test-id="FzeaJR8B">
        <input
          ref={handleRef}
          type="date"
          max="9999-12-31"
          {...rest}
          className={cn(s.root, className, {
            "border-alert": isMissing,
          })}
          test-id="HsUni-cC"
        />

        <button
          type="button"
          className={cn("absolute right-2 top-1/2 -translate-y-1/2 p-1", {
            " opacity-50": rest.disabled,
          })}
          onClick={() => {
            const el = inputRef.current;
            if (!el) return;
            try {
              if (typeof el.showPicker === "function") {
                el.showPicker();
              } else {
                el.focus();
                el.click();
              }
            } catch {
              el.focus();
              el.click();
            }
          }}
          aria-label="Open calendar"
          disabled={rest.disabled}
        >
          <CalendarPickerIcon fill="#fff" />
        </button>

        <div
          className={cn("absolute right-6 top-4", !isLoading && "hidden")}
          test-id="Ezj78rnH"
        >
          <LoadingDots test-id="yNPd4YY6" />
        </div>

        <div
          className={cn("absolute top-3 right-10", {
            hidden: isTodayHidden,
          })}
          test-id="s94gahEz"
        >
          <Button
            variant="primary"
            id={(rest?.id && rest.id + "_") + "today_date"}
            onClick={() => {
              const today = formatDate();
              // Faking the event to dispatch the new value.
              const fakeEvent = constructFakeEvent({ ...rest, value: today });
              rest?.onChange?.(fakeEvent as ChangeEvent<HTMLInputElement>);
              rest?.onBlur?.(fakeEvent as FocusEvent<HTMLInputElement>);
            }}
            className="px-3 py-1"
            disabled={rest.disabled}
            test-id="dCKVP2hB"
          >
            <p className="text-xs font-bold">Today</p>
          </Button>
        </div>
      </div>
    );
  },
);

function padTo2Digits(num: number) {
  return num.toString().padStart(2, "0");
}

function formatDate(date = new Date()) {
  return [
    date.getFullYear(),
    padTo2Digits(date.getMonth() + 1),
    padTo2Digits(date.getDate()),
  ].join("-");
}

export default InputDate;

const constructFakeEvent = ({ ...args }) => ({
  target: {
    value: args?.value,
    name: args?.name,
    id: args?.id,
  },
});
